// https://base-ui.com/react/components/select
import { Select as BaseSelect } from "@base-ui/react/select";
import { Separator as BaseSeparator } from "@base-ui/react/separator";
import { useMemo, useState, type ReactNode } from "react";
import cx from "ui/cx";
import { PopoverContainerContext } from "ui/hooks/usePopoverContainer";
import IconCheck from "ui/icons/IconCheck";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import { childrenOrFallback, consumerRenderHost } from "ui/promoteChildren";
import styles from "./SimpleSelect.module.scss";

/** Value→label map passed to `Select.Root` so `Select.Value` renders the label (not the raw value). */
type SelectItemsMap = React.ComponentProps<typeof BaseSelect.Root>["items"];

interface SelectRootProps {
  children: ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  /**
   * Whether the open dropdown scroll-locks the page and renders a backdrop that consumes the
   * dismissing outside-press. Defaults to Base UI's `true`, which matches Radix Select: clicking
   * outside closes the dropdown first (and not a surrounding modal). Pass `false` only if you need
   * the page to stay scrollable / interactive while the dropdown is open.
   */
  modal?: boolean;
  /** Optional value→label map so the trigger's `Select.Value` shows labels for selected values. */
  items?: SelectItemsMap;
}

/**
 * Trigger height variants: `large` matches the default Input height; `mediumLarge` matches Input's
 * `inputSize="mediumLarge"`; `small` is the compact in-table variant.
 */
type SelectSize = "large" | "mediumLarge" | "small";

interface SelectTriggerProps extends Omit<React.ComponentProps<typeof BaseSelect.Trigger>, "className"> {
  className?: string;
  /**
   * Placeholder for the default value + chevron content. Ignored when a consumer-supplied
   * `render` element is the trigger, since that element supplies its own content.
   */
  placeholder?: string;
  /**
   * Trigger height. Applies only when this part draws the trigger chrome; a consumer-supplied
   * `render` element is the host and sizes itself.
   */
  size?: SelectSize;
  title?: string;
}

interface SelectValueProps extends Omit<React.ComponentProps<typeof BaseSelect.Value>, "className"> {
  className?: string;
}

interface SelectIconProps extends Omit<React.ComponentProps<typeof BaseSelect.Icon>, "className"> {
  className?: string;
}

interface SelectPortalProps {
  children: ReactNode;
  container?: HTMLElement | null;
}

interface SelectContentProps {
  children: ReactNode;
  className?: string;
  /** Radix `position`: "popper" maps to Base UI `alignItemWithTrigger={false}`. */
  position?: "item-aligned" | "popper";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  collisionPadding?: number | Partial<Record<"top" | "right" | "bottom" | "left", number>>;
  sticky?: boolean;
  constrainWidth?: boolean; // sets width to trigger width
  /** Matches the trigger, so the options are typed at the same scale. */
  size?: SelectSize;
}

interface SelectViewportProps {
  children: ReactNode;
  className?: string;
}

interface SelectItemProps extends Omit<React.ComponentProps<typeof BaseSelect.Item>, "className" | "value"> {
  children: ReactNode;
  className?: string;
  value: string;
  tooltip?: ReactNode;
}

interface SelectItemTextProps extends Omit<React.ComponentProps<typeof BaseSelect.ItemText>, "className"> {
  children: ReactNode;
  className?: string;
}

interface SelectItemIndicatorProps extends Omit<React.ComponentProps<typeof BaseSelect.ItemIndicator>, "className"> {
  children?: ReactNode;
  className?: string;
}

interface SelectGroupProps extends Omit<React.ComponentProps<typeof BaseSelect.Group>, "className"> {
  children: ReactNode;
  className?: string;
}

interface SelectLabelProps extends Omit<React.ComponentProps<typeof BaseSelect.GroupLabel>, "className"> {
  children: ReactNode;
  className?: string;
}

interface SelectSeparatorProps extends Omit<React.ComponentProps<typeof BaseSeparator>, "className"> {
  className?: string;
}

// SelectBasic interfaces
interface SelectBasicItem {
  type: "item";
  value: string;
  // ReactNode (not string): consumers render rich labels here (SvgMap's icon+text action items,
  // TaskSidebar's status pills). Only string labels can be shown in the trigger via Base UI's
  // `Select.Value`; non-string labels fall back to the raw value there (see `toValueLabel`), which
  // is fine for custom-trigger / action-menu usage. Provide `textValue` for a trigger-safe string.
  label: ReactNode;
  /** Plain-string label used in the trigger + value matching when `label` is non-string JSX. */
  textValue?: string;
  disabled?: boolean;
  tooltip?: ReactNode;
}

interface SelectBasicGroup {
  type: "group";
  label: ReactNode;
  items: SelectBasicItem[];
}

interface SelectBasicSeparator {
  type: "separator";
}

type SelectBasicItemType = SelectBasicItem | SelectBasicGroup | SelectBasicSeparator;

interface SelectBasicProps
  extends Pick<
      SelectRootProps,
      "value" | "defaultValue" | "onValueChange" | "disabled" | "required" | "name" | "modal"
    >,
    Pick<SelectTriggerProps, "placeholder" | "id" | "size" | "title">,
    Pick<SelectContentProps, "side" | "sideOffset" | "align" | "alignOffset" | "constrainWidth"> {
  items: SelectBasicItemType[];
  className?: string;
  contentClassName?: string;
  triggerClassName?: string;
}

function SelectRoot({
  children,
  value,
  defaultValue,
  onValueChange,
  open,
  defaultOpen,
  onOpenChange,
  name,
  disabled,
  required,
  modal,
  items,
}: SelectRootProps) {
  return (
    // Typed as a single-select string picker so `onValueChange` receives `string | null` (no cast).
    <BaseSelect.Root<string, false>
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange ? (next) => onValueChange(next ?? "") : undefined}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange ? (nextOpen) => onOpenChange(nextOpen) : undefined}
      name={name}
      disabled={disabled}
      required={required}
      modal={modal}
      items={items}
    >
      {children}
    </BaseSelect.Root>
  );
}

function SelectTrigger({
  children,
  className,
  placeholder,
  size = "large",
  nativeButton,
  render,
  ...props
}: SelectTriggerProps) {
  // Promotion is explicit: pass `render={<Capsule/>}` to make your own element the trigger and
  // take over its chrome. Children — one element or several — render inside the trigger and keep
  // our border, padding and state styling, falling back to the value + chevron when there are
  // none. A `render` callback is not a takeover: it supplies the host element and spreads our
  // className back onto it, so our chrome still applies. A `render` element that cannot host props
  // is not a takeover either: it comes back as `demotedChildren` and renders inside our trigger.
  const {
    render: renderHost,
    demotedChildren,
    nativeButton: inferredNativeButton,
    hostOwnsChrome,
  } = consumerRenderHost(render, nativeButton);

  const defaultContent = (
    <>
      <SelectValue placeholder={placeholder} />
      <SelectIcon>
        <IconMiniChevronDown />
      </SelectIcon>
    </>
  );

  return (
    <BaseSelect.Trigger
      className={hostOwnsChrome ? className : cx(styles.trigger, styles[size], className)}
      data-testid="simple-select-trigger"
      nativeButton={inferredNativeButton}
      render={renderHost}
      {...props}
    >
      {hostOwnsChrome ? undefined : childrenOrFallback(demotedChildren ?? children, defaultContent)}
    </BaseSelect.Trigger>
  );
}

function SelectValue({ className, ...props }: SelectValueProps) {
  return <BaseSelect.Value className={cx(styles.value, className)} data-testid="simple-select-value" {...props} />;
}

function SelectIcon({ children, className, ...props }: SelectIconProps) {
  return (
    <BaseSelect.Icon className={cx(styles.icon, className)} data-testid="simple-select-icon" {...props}>
      {children || <IconMiniChevronDown />}
    </BaseSelect.Icon>
  );
}

function SelectPortal({ children, container }: SelectPortalProps) {
  // The portal wrapper carries the z-index so the whole subtree — popup AND the modal-mode
  // backdrop Base UI renders as a sibling of the positioner — sits above our Modal. Without this
  // the backdrop stacks below the modal, the modal's overlay swallows the dismissing click, and
  // both the dropdown and the modal close at once instead of sequentially.
  return (
    <BaseSelect.Portal container={container ?? undefined} className={styles.portal}>
      {children}
    </BaseSelect.Portal>
  );
}

function SelectContent({
  children,
  className,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  position = "popper",
  collisionPadding = 12,
  sticky,
  constrainWidth = false,
  size,
}: SelectContentProps) {
  return (
    <BaseSelect.Positioner
      side={side}
      sideOffset={sideOffset}
      align={align}
      alignOffset={alignOffset}
      collisionPadding={collisionPadding}
      sticky={sticky}
      alignItemWithTrigger={position !== "popper"}
      // Fixed positioning keeps the popup out of the portal container's scroll size (see
      // Popover.tsx); Base UI defaults to `absolute`. The `.portal` wrapper still owns z-index
      // stacking; only the geometry resolves against the viewport.
      positionMethod="fixed"
    >
      <BaseSelect.Popup
        className={cx(styles.content, constrainWidth && styles.constrainWidth, size && styles[size], className)}
        data-testid="simple-select-content"
      >
        {children}
      </BaseSelect.Popup>
    </BaseSelect.Positioner>
  );
}

function SelectViewport({ children, className }: SelectViewportProps) {
  // Base UI has no Viewport part; the popup is the scroll container. We keep a wrapper that both
  // applies our scroll styling and exposes itself as the `PopoverContainerContext` so nested
  // tooltips inside options portal within the listbox (clicks then stay inside the modal/listbox).
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

  return (
    <PopoverContainerContext.Provider value={contentElement}>
      <div ref={setContentElement} className={cx(styles.viewport, className)} data-testid="simple-select-viewport">
        {children}
      </div>
    </PopoverContainerContext.Provider>
  );
}

function SelectItem({ children, className, tooltip, ...props }: SelectItemProps) {
  const item = (
    <BaseSelect.Item
      className={cx(styles.item, className)}
      title={!tooltip && typeof children === "string" ? children : undefined}
      label={typeof children === "string" ? children : undefined}
      data-testid="simple-select-item"
      {...props}
    >
      <SelectItemText>{children}</SelectItemText>
      <SelectItemIndicator>
        <IconCheck />
      </SelectItemIndicator>
    </BaseSelect.Item>
  );

  return wrapWithTooltip(item, tooltip);
}

function SelectItemText({ children, className, ...props }: SelectItemTextProps) {
  return (
    <BaseSelect.ItemText className={cx(styles.itemText, className)} {...props}>
      {children}
    </BaseSelect.ItemText>
  );
}

function SelectItemIndicator({ children, className, ...props }: SelectItemIndicatorProps) {
  return (
    <BaseSelect.ItemIndicator className={cx(styles.itemIndicator, className)} {...props}>
      {children || <IconCheck />}
    </BaseSelect.ItemIndicator>
  );
}

function SelectGroup({ children, className, ...props }: SelectGroupProps) {
  return (
    <BaseSelect.Group className={cx(styles.group, className)} data-testid="simple-select-group" {...props}>
      {children}
    </BaseSelect.Group>
  );
}

function SelectLabel({ children, className, ...props }: SelectLabelProps) {
  return (
    <BaseSelect.GroupLabel className={cx(styles.label, className)} data-testid="simple-select-label" {...props}>
      {children}
    </BaseSelect.GroupLabel>
  );
}

function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return <BaseSeparator className={cx(styles.separator, className)} data-testid="simple-select-separator" {...props} />;
}

type SelectValueLabel = { value: string; label: ReactNode };

const toValueLabel = (item: SelectBasicItem): SelectValueLabel => ({
  value: item.value,
  // The trigger needs a string; prefer an explicit textValue, then a string label, else the value.
  label: item.textValue ?? (typeof item.label === "string" ? item.label : item.value),
});

/** Build the flat value→label map Base UI's `Select.Value` needs to show labels for selected values. */
function buildItemsMap(items: SelectBasicItemType[]): SelectValueLabel[] {
  return items.flatMap((item) => {
    switch (item.type) {
      case "item":
        return [toValueLabel(item)];
      case "group":
        return item.items.map(toValueLabel);
      case "separator":
        return [];
    }
  });
}

function SelectBasic({
  items,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  disabled,
  required,
  name,
  modal,
  className,
  contentClassName,
  triggerClassName,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  constrainWidth = false,
  id,
  size = "large",
  title,
}: SelectBasicProps) {
  const itemsMap = useMemo(() => buildItemsMap(items), [items]);

  const renderItems = (itemList: SelectBasicItemType[]): ReactNode[] => {
    return itemList.map((item, index) => {
      switch (item.type) {
        case "item":
          return (
            <SelectItem
              key={`item-${item.value}-${index}`}
              value={item.value}
              disabled={item.disabled}
              tooltip={item.tooltip}
            >
              {item.label}
            </SelectItem>
          );

        case "group":
          return (
            <SelectGroup key={`group-${index}`}>
              <SelectLabel>{item.label}</SelectLabel>
              {renderItems(item.items)}
            </SelectGroup>
          );

        case "separator":
          return <SelectSeparator key={`separator-${index}`} />;

        default:
          return null;
      }
    });
  };

  return (
    <SelectRoot
      value={value}
      defaultValue={defaultValue}
      onValueChange={!disabled ? onValueChange : undefined}
      disabled={disabled}
      required={required}
      name={name}
      modal={modal}
      items={itemsMap}
    >
      <SelectTrigger
        className={cx(triggerClassName, className)}
        placeholder={placeholder}
        id={id}
        size={size}
        title={title}
      />
      <SelectPortal>
        <SelectContent
          className={contentClassName}
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          constrainWidth={constrainWidth}
          size={size}
        >
          <SelectViewport>{renderItems(items)}</SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  );
}

const SimpleSelect = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Value: SelectValue,
  Icon: SelectIcon,
  Portal: SelectPortal,
  Content: SelectContent,
  Viewport: SelectViewport,
  Item: SelectItem,
  ItemText: SelectItemText,
  ItemIndicator: SelectItemIndicator,
  Group: SelectGroup,
  Label: SelectLabel,
  Separator: SelectSeparator,
  Basic: SelectBasic,
};

export default SimpleSelect;

export type {
  SelectRootProps,
  SelectTriggerProps,
  SelectValueProps,
  SelectIconProps,
  SelectPortalProps,
  SelectContentProps,
  SelectViewportProps,
  SelectItemProps,
  SelectItemTextProps,
  SelectItemIndicatorProps,
  SelectGroupProps,
  SelectLabelProps,
  SelectSeparatorProps,
  SelectBasicProps,
  SelectBasicItem,
  SelectBasicGroup,
  SelectBasicSeparator,
  SelectBasicItemType,
  SelectSize,
};
